import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, ArrowLeft, FlaskConical, TrendingUp, Swords,
  Globe, Briefcase, Map, Bookmark, Loader2, Rocket, ScanEye, Star,
  Smartphone, Lightbulb, AlertTriangle, Target,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { useAIResources } from "../hooks/useAIResources";
import { useAuth } from "../hooks/useAuth";
import { getScanHandoff, saveScanFavorite } from "../lib/api";

const ScorePill = ({ score }) => (
  <div className="inline-flex items-center gap-2 rounded-full bg-[#0F0F0F] text-[#D4AF37] pl-2.5 pr-4 py-1.5" data-testid="ideahub-score">
    <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center text-sm font-extrabold">{score}</span>
    <span className="text-xs font-bold tracking-wide">Opportunity Score</span>
  </div>
);

export default function IdeaHub() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get("id");
  const { patchValidation, patchMarket, patchCompetitor } = useAIResources();
  const { requireAuth } = useAuth();

  const [loading, setLoading] = useState(Boolean(id));
  const [idea, setIdea] = useState(null);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getScanHandoff(id)
      .then(setIdea)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  const name = idea?.startup_name || "Your Startup Idea";
  const description = idea?.description || "";
  const score = idea?.opportunity_score || 0;
  const image = idea?.photo_ref || "";
  const audience = idea?.target_customers || "";
  const problem = idea?.problem || "";

  const toBlueprint = () => {
    window.dispatchEvent(new CustomEvent("lbs:open-blueprint", {
      detail: {
        mode: "scanner_idea",
        idea: {
          name, description, problem, audience,
          build_type: idea?.industry || "",
          revenue_model: idea?.revenue_model || "",
        },
      },
    }));
  };
  const toValidate = () => {
    patchValidation({ form: { name, does: description, customer: audience, problem, different: "" }, report: null });
    toast.success("Idea sent to Validation Lab");
    navigate("/ai-resources/validation-lab");
  };
  const toMarket = () => {
    patchMarket({ idea: `${name} — ${description}`, insights: null });
    toast.success("Idea sent to Market Size Calculator");
    navigate("/ai-resources/market-size");
  };
  const toCompetitors = () => {
    patchCompetitor({ idea: `${name}: ${description}`, result: null });
    toast.success("Idea sent to Competitor Search");
    navigate("/ai-resources/competitor-search");
  };
  const toCountry = () => {
    window.dispatchEvent(new CustomEvent("lbs:open-blueprint", { detail: { mode: "country", country: idea?.country || null } }));
  };
  const toFormation = () => navigate("/resources/business-formation");
  const toRoadmap = () => navigate("/ai-resources/roadmap");
  const saveToNotebook = () => {
    if (saved || saving) return;
    requireAuth(async () => {
      setSaving(true);
      try {
        await saveScanFavorite({
          name, description, ai_score: score, revenue_model: idea?.revenue_model || "",
          problem, audience, industry: idea?.industry || "", photo_ref: image, country: idea?.country || "",
        });
        setSaved(true);
        toast.success("Saved to your Notebook → Collections");
      } catch { toast.error("Could not save. Try again."); }
      finally { setSaving(false); }
    });
  };

  const ACTIONS = [
    { key: "blueprint", label: "Generate Blueprint", desc: "Full AI build plan & prompt", icon: Sparkles, fn: toBlueprint, dark: true },
    { key: "validate", label: "Validation Lab", desc: "Score & validate the idea", icon: FlaskConical, fn: toValidate },
    { key: "market", label: "Market Research", desc: "TAM / SAM / SOM sizing", icon: TrendingUp, fn: toMarket },
    { key: "competitors", label: "Competitor Search", desc: "See who you're up against", icon: Swords, fn: toCompetitors },
    { key: "country", label: "AI Country Analysis", desc: "Localized market insight", icon: Globe, fn: toCountry },
    { key: "formation", label: "Business Formation", desc: "Register & launch legally", icon: Briefcase, fn: toFormation },
    { key: "roadmap", label: "Launch Roadmap", desc: "Your step-by-step path", icon: Map, fn: toRoadmap },
    { key: "save", label: saved ? "Saved ✓" : "Save to Notebook", desc: "Keep it in your library", icon: Bookmark, fn: saveToNotebook },
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 md:py-14">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="ideahub-loading">
            <Loader2 className="animate-spin text-[#D4AF37]" size={38} />
            <p className="text-sm text-[#687280] mt-4">Loading your idea…</p>
          </div>
        ) : error ? (
          <div className="text-center py-24" data-testid="ideahub-error">
            <div className="w-16 h-16 rounded-2xl bg-[#FBF7EA] text-[#8a6d1a] flex items-center justify-center mx-auto"><AlertTriangle size={26} /></div>
            <h1 className="font-display text-2xl font-extrabold text-[#0F0F0F] mt-5">This idea link expired</h1>
            <p className="text-[#687280] mt-2 max-w-md mx-auto">We couldn't find this scanned idea. Try scanning again from the app or generate a fresh one on the web.</p>
            <button onClick={() => navigate("/ai-resources/opportunity-scanner")} className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="ideahub-scan-cta">
              <ScanEye size={16} /> Open Opportunity Scanner
            </button>
          </div>
        ) : idea ? (
          <div data-testid="ideahub-ready">
            {/* Hero */}
            <div className="rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-7 md:p-10 relative overflow-hidden">
              <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(circle at 85% 10%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 55%)" }} />
              <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-7 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#D4AF37]"><Rocket size={14} /> Your Startup Idea Is Ready</span>
                  <h1 className="font-display text-3xl md:text-4xl font-extrabold mt-3" data-testid="ideahub-name">{name}</h1>
                  {description && <p className="text-[#d8d3bf] mt-3 leading-relaxed max-w-xl">{description}</p>}
                  <div className="flex flex-wrap items-center gap-3 mt-5">
                    {score ? <ScorePill score={score} /> : null}
                    {idea.industry && <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/10">{idea.industry}</span>}
                    {idea.revenue_model && <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/10">{idea.revenue_model}</span>}
                  </div>
                </div>
                {image ? (
                  <div className="shrink-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#c9c39f] mb-2 flex items-center gap-1.5"><ScanEye size={12} /> Original Scan</p>
                    <img src={image} alt="Scanned" className="w-full md:w-56 h-44 object-cover rounded-2xl border border-white/10" data-testid="ideahub-image" />
                  </div>
                ) : null}
              </div>
            </div>

            {/* Extra insight strip */}
            {(problem || audience) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                {problem && (
                  <div className="rounded-2xl border border-gray-100 bg-white p-5">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#9aa1ab] flex items-center gap-1.5"><AlertTriangle size={13} className="text-[#D4AF37]" /> Problem</p>
                    <p className="text-sm text-[#374151] mt-2 leading-snug">{problem}</p>
                  </div>
                )}
                {audience && (
                  <div className="rounded-2xl border border-gray-100 bg-white p-5">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#9aa1ab] flex items-center gap-1.5"><Target size={13} className="text-[#D4AF37]" /> Target Customers</p>
                    <p className="text-sm text-[#374151] mt-2 leading-snug">{audience}</p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-9 mb-4">What would you like to do next?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ideahub-actions">
              {ACTIONS.map((a) => (
                <button
                  key={a.key}
                  onClick={a.fn}
                  data-testid={`ideahub-action-${a.key}`}
                  className={`group text-left rounded-2xl p-5 border transition-all hover:-translate-y-0.5 ${
                    a.dark ? "bg-[#0F0F0F] text-white border-[#0F0F0F] hover:bg-black" : "bg-white text-[#0F0F0F] border-gray-100 hover:shadow-[0_16px_40px_rgb(0,0,0,0.08)] hover:border-[#D4AF37]/50"
                  }`}
                >
                  <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${a.dark ? "bg-[#D4AF37] text-[#0F0F0F]" : "bg-[#FBF7EA] text-[#8a6d1a]"}`}><a.icon size={20} /></span>
                  <p className="font-display font-bold mt-4">{a.label}</p>
                  <p className={`text-xs mt-1 ${a.dark ? "text-[#c9c39f]" : "text-[#687280]"}`}>{a.desc}</p>
                  <span className={`inline-flex items-center gap-1 text-xs font-bold mt-3 ${a.dark ? "text-[#D4AF37]" : "text-[#B8901F]"}`}>Continue <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" /></span>
                </button>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
              <button onClick={() => navigate("/ai-resources/opportunity-scanner")} className="inline-flex items-center gap-2 text-sm font-bold text-[#374151] hover:text-[#0F0F0F] transition-colors" data-testid="ideahub-back-scan">
                <ArrowLeft size={15} /> Scan another idea
              </button>
              <button onClick={() => navigate("/notebook")} className="inline-flex items-center gap-2 text-sm font-bold text-[#B8901F] hover:underline" data-testid="ideahub-open-notebook">
                <Bookmark size={15} /> Open Startup Notebook
              </button>
            </div>
          </div>
        ) : (
          // No ?id= — funnel intro (mobile app entry point)
          <div className="text-center py-8" data-testid="ideahub-intro">
            <div className="rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-9 md:p-14 relative overflow-hidden">
              <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(circle at 50% 12%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 55%)" }} />
              <div className="relative max-w-2xl mx-auto">
                <span className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 text-[#D4AF37] flex items-center justify-center mx-auto"><Lightbulb size={30} /></span>
                <h1 className="font-display text-3xl md:text-5xl font-extrabold mt-6">The LaunchBS Idea Hub</h1>
                <p className="text-[#d8d3bf] mt-4 text-base md:text-lg leading-relaxed">Scanned a real-world opportunity with the LaunchBS AI Scanner app? Tap <span className="text-[#D4AF37] font-bold">Validate on LaunchBS</span> and your idea lands right here — ready to blueprint, validate, research and build.</p>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
                  <button onClick={() => navigate("/ai-resources/opportunity-scanner")} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="ideahub-intro-scan">
                    <ScanEye size={16} /> Try the Web Scanner
                  </button>
                  <span className="inline-flex items-center gap-2 text-sm text-[#c9c39f] font-semibold"><Smartphone size={16} className="text-[#D4AF37]" /> Mobile app coming soon</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8 text-left">
              {[
                { icon: ScanEye, t: "Scan", d: "Snap anything with the app" },
                { icon: Sparkles, t: "Discover", d: "AI finds 5 startup ideas" },
                { icon: Rocket, t: "Validate", d: "Send one to the Idea Hub" },
                { icon: Bookmark, t: "Organize", d: "Save into your Notebook" },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl border border-gray-100 bg-white p-5">
                  <span className="w-10 h-10 rounded-xl bg-[#FBF7EA] text-[#8a6d1a] flex items-center justify-center"><s.icon size={18} /></span>
                  <p className="font-display font-bold text-[#0F0F0F] mt-3">{s.t}</p>
                  <p className="text-xs text-[#687280] mt-1">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
